// NOTICE: KAPTION-INTEGRATION
// Shared language constants for live translation feature
// Contains language codes, names, and flag emojis
//
// ============================================================================
// ⚠️  IMPORTANT: This list must stay in sync with the backend!
//
// 📖 Documentation: kaption/docs/KAPTION_LANGUAGE_SUPPORT.md
//
// Files that must match this list:
//   - kaption/app/adapters/kaption_translation_adapter.rb (SUPPORTED_LANGUAGES)
//   - app/javascript/dashboard/components-next/message/kaption/KaptionTranslationIndicator.vue
//
// If you add/remove languages here, follow the steps in the documentation.
// ============================================================================

/**
 * Language to country code mapping - maps language codes to ISO 3166-1 alpha-2 country codes
 * Used for circle-flags SVG icons
 */
export const LANGUAGE_TO_COUNTRY = {
  ar: 'sa', // Arabic -> Saudi Arabia
  cs: 'cz', // Czech -> Czech Republic
  da: 'dk', // Danish -> Denmark
  de: 'de', // German -> Germany
  el: 'gr', // Greek -> Greece
  en: 'gb', // English -> Great Britain
  es: 'es', // Spanish -> Spain
  fa: 'ir', // Farsi -> Iran
  fi: 'fi', // Finnish -> Finland
  fr: 'fr', // French -> France
  he: 'il', // Hebrew -> Israel
  hi: 'in', // Hindi -> India
  id: 'id', // Indonesian -> Indonesia
  it: 'it', // Italian -> Italy
  ja: 'jp', // Japanese -> Japan
  ko: 'kr', // Korean -> South Korea
  ms: 'my', // Malay -> Malaysia
  nl: 'nl', // Dutch -> Netherlands
  no: 'no', // Norwegian -> Norway
  pl: 'pl', // Polish -> Poland
  pt: 'br', // Portuguese -> Brazil (primary market)
  ru: 'ru', // Russian -> Russia
  sv: 'se', // Swedish -> Sweden
  th: 'th', // Thai -> Thailand
  tr: 'tr', // Turkish -> Turkey
  vi: 'vn', // Vietnamese -> Vietnam
  zh: 'cn', // Chinese -> China
};

/**
 * Get country code for a language code
 * @param {string} languageCode - Two-letter language code (e.g., 'en', 'es')
 * @returns {string|null} Country code or null as fallback
 */
export function getCountryCode(languageCode) {
  return LANGUAGE_TO_COUNTRY[languageCode] || null;
}

/**
 * Language emoji mapping - maps language codes to flag emojis
 */
export const LANGUAGE_EMOJIS = {
  en: '🇬🇧',
  es: '🇪🇸',
  pt: '🇧🇷', // NOTICE: KAPTION-INTEGRATION - Changed to Brazil flag for Portuguese
  fr: '🇫🇷',
  de: '🇩🇪',
  it: '🇮🇹',
  ru: '🇷🇺',
  ar: '🇸🇦',
  zh: '🇨🇳',
  ja: '🇯🇵',
  ko: '🇰🇷',
  hi: '🇮🇳',
  nl: '🇳🇱',
  pl: '🇵🇱',
  vi: '🇻🇳',
  th: '🇹🇭',
  tr: '🇹🇷',
  sv: '🇸🇪',
  da: '🇩🇰',
  fi: '🇫🇮',
  no: '🇳🇴',
  el: '🇬🇷',
  he: '🇮🇱',
  id: '🇮🇩',
  ms: '🇲🇾',
  fa: '🇮🇷',
  cs: '🇨🇿',
};

/**
 * Available languages for translation feature
 * Each language has a code, name, and flag emoji
 */
export const AVAILABLE_LANGUAGES = [
  { code: 'en', name: 'English', flag: LANGUAGE_EMOJIS.en },
  { code: 'es', name: 'Español', flag: LANGUAGE_EMOJIS.es },
  { code: 'pt', name: 'Português', flag: LANGUAGE_EMOJIS.pt },
  { code: 'fr', name: 'Français', flag: LANGUAGE_EMOJIS.fr },
  { code: 'de', name: 'Deutsch', flag: LANGUAGE_EMOJIS.de },
  { code: 'it', name: 'Italiano', flag: LANGUAGE_EMOJIS.it },
  { code: 'ru', name: 'Русский', flag: LANGUAGE_EMOJIS.ru },
  { code: 'ar', name: 'العربية', flag: LANGUAGE_EMOJIS.ar },
  { code: 'zh', name: '中文', flag: LANGUAGE_EMOJIS.zh },
  { code: 'ja', name: '日本語', flag: LANGUAGE_EMOJIS.ja },
  { code: 'ko', name: '한국어', flag: LANGUAGE_EMOJIS.ko },
  { code: 'hi', name: 'हिन्दी', flag: LANGUAGE_EMOJIS.hi },
  { code: 'nl', name: 'Nederlands', flag: LANGUAGE_EMOJIS.nl },
  { code: 'pl', name: 'Polski', flag: LANGUAGE_EMOJIS.pl },
  { code: 'vi', name: 'Tiếng Việt', flag: LANGUAGE_EMOJIS.vi },
  { code: 'th', name: 'ไทย', flag: LANGUAGE_EMOJIS.th },
  { code: 'tr', name: 'Türkçe', flag: LANGUAGE_EMOJIS.tr },
  { code: 'sv', name: 'Svenska', flag: LANGUAGE_EMOJIS.sv },
  { code: 'da', name: 'Dansk', flag: LANGUAGE_EMOJIS.da },
  { code: 'fi', name: 'Suomi', flag: LANGUAGE_EMOJIS.fi },
  { code: 'no', name: 'Norsk', flag: LANGUAGE_EMOJIS.no },
  { code: 'el', name: 'Ελληνικά', flag: LANGUAGE_EMOJIS.el },
  { code: 'he', name: 'עברית', flag: LANGUAGE_EMOJIS.he },
  { code: 'id', name: 'Bahasa Indonesia', flag: LANGUAGE_EMOJIS.id },
  { code: 'ms', name: 'Bahasa Melayu', flag: LANGUAGE_EMOJIS.ms },
  { code: 'fa', name: 'فارسی', flag: LANGUAGE_EMOJIS.fa },
  { code: 'cs', name: 'Čeština', flag: LANGUAGE_EMOJIS.cs },
];

/**
 * Get flag emoji for a language code
 * @param {string} languageCode - Two-letter language code (e.g., 'en', 'es')
 * @returns {string} Flag emoji or 🌐 as fallback
 */
export function getLanguageFlag(languageCode) {
  return LANGUAGE_EMOJIS[languageCode] || '🌐';
}

/**
 * Get language name for a language code
 * @param {string} languageCode - Two-letter language code (e.g., 'en', 'es')
 * @returns {string} Language name or 'Unknown' as fallback
 */
export function getLanguageName(languageCode) {
  const lang = AVAILABLE_LANGUAGES.find(l => l.code === languageCode);
  return lang ? lang.name : 'Unknown';
}

/**
 * Default language code (Spanish)
 */
export const DEFAULT_LANGUAGE = 'es';
